const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

class CustomBlogListing extends HTMLElement {
  constructor() {
    super();
    this.debounceTimer = null;
    this.originalGridHTML = null;
    this.controller = null;
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.input = this.querySelector('[data-search-input]');
    this.resetButton = this.querySelector('[data-search-reset]');
    this.emptyState = this.querySelector('[data-search-empty]');
    this.status = this.querySelector('[data-search-status]');
    this.form = this.querySelector('[data-search-form]');
    this.activeTag = this.dataset.activeTag || '';

    if (!this.input || !this.grid) return;
    this.originalGridHTML = this.grid.innerHTML;

    this.input.addEventListener('input', this.handleInput.bind(this));
    this.resetButton?.addEventListener('click', this.handleReset.bind(this));
    this.form?.addEventListener('submit', (e) => e.preventDefault());
    this.grid.addEventListener('click', this.handleGridClick.bind(this));
  }

  async handleGridClick(e) {
    const link = e.target.closest('[data-search-pagination] a');
    if (!link) return;
    e.preventDefault();
    const url = new URL(link.href, window.location.origin);
    const page = url.searchParams.get('page') || '1';
    const query = this.input.value.trim();
    if (query.length < MIN_QUERY_LENGTH) return;
    await this.runSearch(query, page);
    this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  handleInput() {
    const query = this.input.value.trim();
    if (this.resetButton) this.resetButton.hidden = query.length === 0;
    clearTimeout(this.debounceTimer);
    if (query.length < MIN_QUERY_LENGTH) {
      this.restoreOriginalGrid();
      return;
    }
    this.debounceTimer = setTimeout(() => this.runSearch(query), DEBOUNCE_MS);
  }

  handleReset() {
    this.input.value = '';
    if (this.resetButton) this.resetButton.hidden = true;
    this.restoreOriginalGrid();
    this.input.focus();
  }

  restoreOriginalGrid() {
    clearTimeout(this.debounceTimer);
    this.controller?.abort();
    this.removeAttribute('data-search-active');
    if (this.emptyState) this.emptyState.hidden = true;
    if (this.status) this.status.textContent = '';
    this.grid.innerHTML = this.originalGridHTML;
  }

  async runSearch(query, page = '1') {
    // Cancel any in-flight request so out-of-order responses can't clobber the
    // latest query, and a cleared input can't be repopulated by a stale fetch.
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    const q = this.activeTag ? `tag:${this.activeTag} ${query}` : query;
    const params = new URLSearchParams({
      q,
      type: 'article',
      sections: 'blog-search-results',
    });
    if (page !== '1') params.set('page', page);

    try {
      const response = await fetch(`/search?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      this.renderResults(data['blog-search-results']);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Blog search failed', err);
      this.renderResults(null);
    }
  }

  renderResults(html) {
    this.setAttribute('data-search-active', '');

    let innerHTML = '';
    let count = 0;
    if (html) {
      const temp = document.createElement('div');
      temp.innerHTML = html;
      const inner = temp.querySelector('[data-search-section]');
      innerHTML = inner ? inner.innerHTML.trim() : '';
      count = inner ? inner.querySelectorAll('.custom-content-card').length : 0;
    }

    if (!innerHTML) {
      if (this.emptyState) this.emptyState.hidden = false;
      this.grid.innerHTML = '';
      this.announce('No articles match your search.');
      return;
    }

    if (this.emptyState) this.emptyState.hidden = true;
    this.grid.innerHTML = innerHTML;
    this.announce(`${count} article${count === 1 ? '' : 's'} found.`);
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('custom-blog-listing')) {
  customElements.define('custom-blog-listing', CustomBlogListing);
}
